import { Eye, EyeOff, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { convertToNumber } from "../CommonComponents/CustomHooks";
import { InputBox } from "../CommonComponents/InputBox";
import Loader from './Loader';
import './LoginCSS.css';
import { LockOpen, Building } from 'lucide-react';
import { getActiveProject } from '../CommonComponents/ProjectConfig';

const ForgotPassword = () => {
    const activeProject = getActiveProject();
    const StaticToken = 'AmqCLI6rg7olYF5gNsde1eRE';
    const navigate = useNavigate();
    const inputRefs = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfermPassword, setShowConfermPassword] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotData, setForgotData] = useState({
        userCode: '',
        OTP: '',
        newPassword: '',
        confirmPassword: '',
        userType: "client", 
    });
    const handleSendOTP = async () => {
        setForgotLoading(true);
        try {
            const response = await fetch(`/api/UserLogin/SendForgotPasswordOTP`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    AuthToken: StaticToken,
                    UserType: forgotData.userType,
                    UserCode: forgotData.userCode
                })
            });
            const res = await response.json();
            if (res.status === "SUCCESS") {

                toast.success(res.message);
                setCurrentStep(2);
            } else {
                toast.error(res.message);
                inputRefs.current[res.focus]?.focus();
            }
        } catch (error) {
            toast.error(String(error));
        } finally {
            setForgotLoading(false);
        }
    };
    const handleVerifyOTP = async () => {
        setForgotLoading(true);
        try {
            const response = await fetch(`/api/UserLogin/ResetPassword`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    AuthToken: StaticToken, 
                    UserType: forgotData.userType,
                    UserCode: forgotData.userCode,
                    OTP: forgotData.OTP,
                    Type: "VerifyOTP"
                })
            });
            const res = await response.json();
            if (res.status === "SUCCESS") {
                toast.success(res.message);
                setCurrentStep(3);
            } else {
                toast.error(res.message);
                setForgotData({
                    ...forgotData,
                    OTP: ''
                });
                inputRefs.current[res.focus]?.focus();
            }
        } catch (error) {
            toast.error(String(error));
        } finally {
            setForgotLoading(false);
        }
    };
    const handleReset = async () => {
        setForgotLoading(true);
        try {
            const response = await fetch(`/api/UserLogin/ResetPassword`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    AuthToken: StaticToken, 
                    UserCode: forgotData.userCode,
                    OTP: forgotData.OTP,
                    UserType: forgotData.userType,
                    Type: "ResetPassword",
                    NewPassword: forgotData.newPassword,
                    ConfirmPassword: forgotData.confirmPassword
                })
            });
            const res = await response.json();
            if (res.status === "SUCCESS") {
                toast.success(res.message);
                setTimeout(() => {
                    setCurrentStep(1);
                    setForgotData({
                        userCode: '',
                        OTP: '',
                        newPassword: '',
                        confirmPassword: ''
                    });
                    navigate('/login');
                }, 800);
            } else {
                toast.error(res.message);
                inputRefs.current[res.focus]?.focus();
            }
        } catch (error) {
            toast.error(String(error));
        } finally {
            setForgotLoading(false);
        }
    };
    useEffect(() => {
        if (currentStep === 1) {
            inputRefs.current['txtUserCode']?.focus();
        }
        if (currentStep === 2) {
            setTimeout(() => {
                inputRefs.current['txtOTP']?.focus();
            }, 100);
        }
        if (currentStep === 3) {
            setTimeout(() => {
                inputRefs.current['txtNewPassword']?.focus();
            }, 100);
        }
    }, [currentStep]);
    return (
        <>
            <div className="min-h-screen">
                <div className="grid grid-cols-1 md:grid-cols-3 min-h-screen">
                    <div className="col-span-2 relative bg-[var(--theme-color)] p-20">
                        <div className="absolute hidden md:block bottom-0 left-0 w-full">
                            <img src={activeProject.loginBg} alt='' className='w-full' />
                            <div className="absolute top-0 left-0 w-full h-full" style={{ background: "linear-gradient(to bottom, var(--theme-color), transparent)" }}>

                            </div>
                        </div>
                        <div className="relative z-10">
                            {activeProject.establishedYear && (
                                <span className="px-4 py-2 bg-white text-[var(--theme-color)] font-semibold rounded-full inline-flex items-center gap-2"><Sparkles width={16} /> Established in {activeProject.establishedYear}</span>
                            )}
                            <h1 className="text-white text-4xl font-bold mt-4">{activeProject.loginHeadingLine1} <br/> {activeProject.loginHeadingLine2}</h1>
                            <p className="text-white text-base mt-3">{activeProject.description}</p>
                        </div>
                    </div>
                    <div>
                        <div className="w-full relative flex items-center justify-center px-6 py-12 overflow-hidden h-full">
                            <div className="relative z-10 w-full max-w-md p-8 rounded-3xl">
                                <div className="mb-5">
                                    <img src={activeProject.logo} className="w-[240px] mx-auto" alt={`${activeProject.name} logo`} />
                                </div>
                                <div className="mb-8 text-center fade-up">
                                    <div className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-[11px] font-medium text-slate-500 tracking-wider">
                                        <Building size={13} />
                                        CLIENT PANEL
                                    </div>

                                    <h2 className="text-[22px] font-semibold text-slate-800">Account recovery</h2>
                                    <p className="text-slate-400 text-sm mt-1">Access your logistics dashboard securely</p>
                                </div>
                                <div className="space-y-5">
                                    <div className="flex items-center justify-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm text-white ${currentStep >= 1 ? 'bg-[var(--theme-color)]' : 'bg-gray-300'}`}>
                                            1
                                        </div>
                                        <div className="w-10 h-[2px] bg-gray-300">
                                        </div>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm text-white ${currentStep >= 2 ? 'bg-[var(--theme-color)]' : 'bg-gray-300'}`}>
                                            2
                                        </div>
                                        <div className="w-10 h-[2px] bg-gray-300"></div>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm text-white ${currentStep >= 3 ? 'bg-[var(--theme-color)]' : 'bg-gray-300'}`}> 3 </div>
                                    </div>
                                    {currentStep === 1 && (<div className="space-y-4">
                                        <InputBox
                                            id="txtUserCode"
                                            label="User Code"
                                            maxLength='10'
                                            placeholder="Enter User Code"
                                            value={forgotData.userCode}
                                            onChange={(e) => setForgotData({ ...forgotData, userCode: (e.target.value) })}
                                            inputrefs={inputRefs}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    inputRefs.current['OTPbutton']?.focus();
                                                }
                                            }}
                                        />
                                        <button
                                            ref={(ref) => (inputRefs.current['OTPbutton'] = ref)}
                                            id='OTPbutton'
                                            onClick={handleSendOTP}
                                            className="w-full bg-[var(--theme-color)] text-white py-2 rounded-xl" >
                                            {forgotLoading ? 'Sending OTP...' : 'Send OTP'}
                                        </button>
                                    </div>)}
                                    {currentStep === 2 && (<div className="space-y-4">
                                        <InputBox
                                            maxLength='6'
                                            id="txtOTP"
                                            label="OTP"
                                            placeholder="Enter OTP"
                                            value={forgotData.OTP}
                                            onChange={(e) => setForgotData({ ...forgotData, OTP: convertToNumber(e.target.value) })}
                                            inputrefs={inputRefs}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    inputRefs.current['verifyOTP']?.focus();
                                                }
                                            }}
                                        />
                                        <button
                                            ref={(ref) => (inputRefs.current['verifyOTP'] = ref)}
                                            id='verifyOTP'
                                            onClick={handleVerifyOTP}
                                            className="w-full bg-[var(--theme-color)] text-white py-2 rounded-xl" >
                                            {forgotLoading ? 'Verifying...' : 'Verify OTP'}
                                        </button>
                                    </div>)}
                                    {currentStep === 3 && (<div className="space-y-4">
                                        <div className="relative">
                                            <InputBox
                                                id="txtNewPassword"
                                                label="New Password"
                                                type={showNewPassword ? "text" : "password"}
                                                placeholder="Enter New Password"
                                                value={forgotData.newPassword}
                                                onChange={(e) => setForgotData({ ...forgotData, newPassword: e.target.value })}
                                                inputrefs={inputRefs}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        inputRefs.current['txtConfirmPassword']?.focus();
                                                    }
                                                }}
                                            />
                                            <span
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-[22px] cursor-pointer text-slate-400 hover:text-[var(--theme-color)]"
                                            >
                                                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </span>
                                        </div>
                                        <div className="relative">
                                            <InputBox
                                                id="txtConfirmPassword"
                                                label="Confirm Password"
                                                type={showConfermPassword ? "text" : "password"}
                                                placeholder="Confirm Password"
                                                value={forgotData.confirmPassword}
                                                onChange={(e) => setForgotData({ ...forgotData, confirmPassword: e.target.value })}
                                                inputrefs={inputRefs}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        inputRefs.current['resetButton']?.focus();
                                                    }
                                                }}
                                            />
                                            <span
                                                onClick={() => setShowConfermPassword(!showConfermPassword)}
                                                className="absolute right-3 top-[22px] cursor-pointer text-slate-400 hover:text-[var(--theme-color)]"
                                            >
                                                {showConfermPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </span>
                                        </div>
                                        <button
                                            ref={(ref) => (inputRefs.current['resetButton'] = ref)}
                                            id='resetButton'
                                            onClick={handleReset}
                                            className="w-full bg-[var(--theme-color)] text-white py-2 rounded-xl" >
                                            {forgotLoading ? 'Resetting...' : 'Reset Password'}
                                        </button>
                                    </div>)}
                                    <button type="button"
                                        onClick={() => { navigate('/'); setCurrentStep(1); }}
                                        className="w-full border border-gray-300 py-2 rounded-xl hover:bg-gray-50" > Back To Login </button>
                                </div>
                                <p className="mt-8 text-center text-xs text-slate-400">
                                    © {new Date().getFullYear()} {activeProject.copyright}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isLoading && (
                <Loader text={activeProject.welcomeMessage} size='100px' img_path={activeProject.favicon} />
            )}
            <ToastContainer />
        </>
    )
}
export default ForgotPassword;